/**
 * GameHost — main-thread controller for one game slot.
 *
 * Creates the worker, hands it the OffscreenCanvases (GL + HUD) and the SharedArrayBuffer
 * input ring, fetches the game bytes (through the Service Worker cache), and exposes the
 * lifecycle the Carousel drives:
 *
 *   load()    fetch -> worker init -> 'ready' (compiled, blitz_init done, tick 0 drawn, paused)
 *   resume()  start the micro-session (new session uuid)
 *   pause()   stop ticking (leaving viewport / idle / tab hidden)
 *   exit()    end the micro-session with an exit condition -> final telemetry batch
 *   destroy() terminate the worker, drop canvases — no leaks
 */
import type { GameMeta, HostState, MainToWorker, ReadyMessage, WorkerToMain } from './protocol';
import { InputWriter, createInputBuffer, isSharedInputAvailable, packRecords, epochNow, type InputRecord } from './InputPipeline';
import type { ExitCondition, LiveStats, MechanicalTelemetry } from '../telemetry/TelemetryTypes';
import { nextHostSerial } from './ContentHost';

export interface GameHostOptions {
  width: number;
  height: number;
  dpr: number;
  userId: string;
  debug?: boolean;
  /** Force the postMessage input path even when SAB is available (dev toolbar toggle). */
  forcePostMessage?: boolean;
  fetchBytes?: (url: string) => Promise<ArrayBuffer>;
}

export interface GameHostEvents {
  state: (state: HostState, host: GameHost) => void;
  ready: (info: ReadyMessage, host: GameHost) => void;
  stats: (stats: LiveStats, host: GameHost) => void;
  telemetry: (batch: MechanicalTelemetry, host: GameHost) => void;
  score: (score: number, host: GameHost) => void;
  haptic: (ms: number, host: GameHost) => void;
  error: (message: string, phase: string, host: GameHost) => void;
  log: (message: string, host: GameHost) => void;
}

type Listener<K extends keyof GameHostEvents> = GameHostEvents[K];

export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const WASM_MAGIC = [0x00, 0x61, 0x73, 0x6d];

async function defaultFetchBytes(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch ${url}: HTTP ${res.status}`);
  const bytes = await res.arrayBuffer();
  if (/\.wasm(\?|$)/.test(url) || url.startsWith('blob:')) {
    const head = new Uint8Array(bytes, 0, Math.min(4, bytes.byteLength));
    if (bytes.byteLength < 8 || !WASM_MAGIC.every((b, i) => head[i] === b)) {
      const type = res.headers.get('content-type') ?? 'unknown';
      throw new Error(`${url} is not a WebAssembly binary (content-type ${type}) — is the game built? (npm run build:games)`);
    }
  }
  return bytes;
}

export class GameHost {
  readonly serial = nextHostSerial();
  readonly meta: GameMeta;
  state: HostState = 'idle';
  sessionId = '';
  ready: ReadyMessage | null = null;
  lastStats: LiveStats | null = null;
  lastError: { phase: string; message: string } | null = null;
  score = 0;
  loadStartedAt = 0;
  readyAt = 0;
  lastInputAt = 0;
  liked = false;

  private worker: Worker | null = null;
  private writer: InputWriter | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private hud: HTMLCanvasElement | null = null;
  private listeners: { [K in keyof GameHostEvents]?: Set<Listener<K>> } = {};
  private readyResolve: ((r: ReadyMessage) => void) | null = null;
  private readyReject: ((e: Error) => void) | null = null;
  private opts: GameHostOptions;
  readonly inputMode: 'sab' | 'postMessage';

  constructor(meta: GameMeta, opts: GameHostOptions) {
    this.meta = meta;
    this.opts = opts;
    this.inputMode = !opts.forcePostMessage && isSharedInputAvailable() ? 'sab' : 'postMessage';
  }

  on<K extends keyof GameHostEvents>(event: K, fn: Listener<K>): () => void {
    const bag = this.listeners as Record<string, Set<Function> | undefined>;
    const set = (bag[event] ??= new Set<Function>());
    set.add(fn);
    return () => set.delete(fn);
  }

  private emit<K extends keyof GameHostEvents>(event: K, ...args: Parameters<Listener<K>>) {
    const set = this.listeners[event] as Set<(...a: unknown[]) => void> | undefined;
    if (!set) return;
    for (const fn of set) fn(...args);
  }

  private setState(s: HostState) {
    if (this.state === s) return;
    this.state = s;
    this.emit('state', s, this);
  }

  get workerAlive(): boolean {
    return this.worker !== null;
  }

  /**
   * Attach the DOM canvases the slide rendered. Both are transferred to the worker as
   * OffscreenCanvases in load(); a transferred canvas can never be reused, so the slide
   * creates fresh elements per GameHost.
   */
  attachCanvases(canvas: HTMLCanvasElement, hud: HTMLCanvasElement) {
    this.canvas = canvas;
    this.hud = hud;
    const { width, height, dpr } = this.opts;
    for (const c of [canvas, hud]) {
      c.width = Math.round(width * dpr);
      c.height = Math.round(height * dpr);
    }
  }

  async load(): Promise<ReadyMessage> {
    if (this.state !== 'idle') throw new Error(`load() called in state ${this.state}`);
    if (!this.canvas || !this.hud) throw new Error('attachCanvases() before load()');
    this.setState('loading');
    this.loadStartedAt = performance.now();
    const fetchBytes = this.opts.fetchBytes ?? defaultFetchBytes;

    let wasm: ArrayBuffer;
    let data: ArrayBuffer | null = null;
    try {
      [wasm, data] = await Promise.all([
        fetchBytes(this.meta.wasm_url),
        this.meta.data_url ? fetchBytes(this.meta.data_url) : Promise.resolve(null),
      ]);
    } catch (err) {
      this.fail('fetch', err instanceof Error ? err.message : String(err));
      throw err;
    }
    if ((this.state as HostState) !== 'loading') throw new Error('destroyed during fetch');

    const worker = new Worker(new URL('../workers/GameWorker.ts', import.meta.url), {
      type: 'module',
      name: `blitz-game-${this.meta.id}`,
    });
    this.worker = worker;
    worker.onmessage = (ev: MessageEvent<WorkerToMain>) => this.onWorkerMessage(ev.data);
    worker.onerror = (ev) => this.fail('worker', ev.message || 'worker error');

    const offscreen = this.canvas.transferControlToOffscreen();
    const offscreenHud = this.hud.transferControlToOffscreen();

    let input: SharedArrayBuffer | null = null;
    if (this.inputMode === 'sab') {
      input = createInputBuffer();
      this.writer = new InputWriter(input);
    }

    const readyPromise = new Promise<ReadyMessage>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });

    const init: MainToWorker = {
      type: 'init',
      gameId: this.meta.id,
      canvas: offscreen,
      hud: offscreenHud,
      width: this.opts.width,
      height: this.opts.height,
      dpr: this.opts.dpr,
      wasm,
      data,
      input,
      seed: (Math.random() * 0xffffffff) >>> 0,
      sessionId: uuid(),
      userId: this.opts.userId,
      debug: !!this.opts.debug,
    };
    const transfer: Transferable[] = [offscreen, offscreenHud, wasm];
    if (data) transfer.push(data);
    worker.postMessage(init, transfer);
    return readyPromise;
  }

  private onWorkerMessage(msg: WorkerToMain) {
    switch (msg.type) {
      case 'ready':
        this.ready = msg;
        this.readyAt = performance.now();
        this.setState('ready');
        this.readyResolve?.(msg);
        this.readyResolve = this.readyReject = null;
        this.emit('ready', msg, this);
        break;
      case 'error':
        this.fail(msg.phase, msg.message);
        break;
      case 'telemetry':
        this.emit('telemetry', msg.batch, this);
        break;
      case 'stats':
        this.lastStats = msg.stats;
        this.emit('stats', msg.stats, this);
        break;
      case 'score':
        this.score = msg.score;
        this.emit('score', msg.score, this);
        break;
      case 'haptic':
        this.emit('haptic', msg.ms, this);
        break;
      case 'log':
        this.emit('log', msg.message, this);
        break;
      case 'destroyed':
        break;
      case 'pong':
        break;
    }
  }

  private fail(phase: string, message: string) {
    this.lastError = { phase, message };
    this.setState('crashed');
    this.readyReject?.(new Error(`${phase}: ${message}`));
    this.readyResolve = this.readyReject = null;
    this.emit('error', message, phase, this);
  }

  /** Start (or restart after pause/idle) a micro-session. */
  resume() {
    if (!this.worker) return;
    if (this.state !== 'ready' && this.state !== 'paused') return;
    this.sessionId = uuid();
    this.lastInputAt = performance.now();
    this.worker.postMessage({ type: 'resume', sessionId: this.sessionId } satisfies MainToWorker);
    this.setState('running');
  }

  pause(reason: 'swipe' | 'idle' | 'hidden' | 'manual' = 'manual') {
    if (!this.worker || this.state !== 'running') return;
    this.worker.postMessage({ type: 'pause', reason } satisfies MainToWorker);
    this.setState('paused');
  }

  /**
   * The card has left the screen. A paused worker does nothing — no ticks, no draws, and the
   * wasm sandbox has no audio — so pausing is all "sleep" means here. (WebCardHost has to
   * unload its page for the same call; see there.)
   */
  sleep() {
    if (this.state === 'running') this.pause('swipe');
  }

  /** End the current micro-session with a reason; the worker emits the final batch. */
  exit(condition: ExitCondition) {
    if (!this.worker) return;
    if (this.state === 'running') this.pause(condition === 'IDLE_TIMEOUT' ? 'idle' : 'swipe');
    this.worker.postMessage({ type: 'exit', condition } satisfies MainToWorker);
  }

  resize(width: number, height: number, dpr: number) {
    this.opts = { ...this.opts, width, height, dpr };
    this.worker?.postMessage({ type: 'resize', width, height, dpr } satisfies MainToWorker);
  }

  debugCrash() {
    this.worker?.postMessage({ type: 'debug-crash' } satisfies MainToWorker);
  }

  /** Zero-copy input: writes a record into the SAB ring (or posts it on the fallback path). */
  input(kind: number, id: number, x: number, y: number, t: number = epochNow()): boolean {
    if (!this.worker || this.state !== 'running') return false;
    this.lastInputAt = performance.now();
    if (this.writer) return this.writer.push(kind, id, x, y, t);
    const rec: InputRecord = { kind, id, x, y, t, wt: epochNow() };
    const buf = packRecords([rec]);
    this.worker.postMessage({ type: 'input', records: buf } satisfies MainToWorker, [buf]);
    return true;
  }

  gyro(beta: number, gamma: number, alpha: number) {
    this.writer?.setSensor(beta, gamma, alpha);
  }

  get droppedInputs(): number {
    return this.writer?.dropped ?? 0;
  }

  /** Terminate the worker and release the canvases. Safe to call twice. */
  destroy() {
    if (this.state === 'destroyed') return;
    const worker = this.worker;
    this.worker = null;
    if (worker) {
      try {
        worker.postMessage({ type: 'destroy' } satisfies MainToWorker);
      } catch {
        /* worker may already be gone */
      }
      // Give the worker a moment to flush its final telemetry batch (still delivered to the
      // 'telemetry' listeners), then hard-terminate and drop every listener.
      setTimeout(() => {
        worker.terminate();
        this.listeners = {};
      }, 250);
    } else {
      this.listeners = {};
    }
    this.writer = null;
    this.canvas?.remove();
    this.hud?.remove();
    this.canvas = null;
    this.hud = null;
    this.readyReject?.(new Error('destroyed'));
    this.readyResolve = this.readyReject = null;
    this.setState('destroyed');
  }
}
